import React, { useEffect } from 'react'
import { useStore } from 'effector-react'
import { Table, Card, Row, Col, Statistic, Tag, Button, Collapse, Typography, Spin, Alert } from 'antd'
import { ReloadOutlined, DatabaseOutlined, WarningOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { $diagnostics, $diagnosticsLoading, diagnosticsMounted, getDiagnostics } from './model'
import type { SlowQuery, MissingIndex, ActiveConnection, TableBloat, UnusedIndex, WaitingLock, VacuumStatusRow, WorkerProcess, PgSetting } from './types'

const { Text } = Typography

const slowQueryColumns: ColumnsType<SlowQuery> = [
  {
    title: 'Query',
    dataIndex: 'query',
    width: '40%',
    render: (text: string) => (
      <Text code style={{ fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
        {text?.length > 200 ? text.slice(0, 200) + '...' : text}
      </Text>
    ),
  },
  {
    title: 'Calls',
    dataIndex: 'calls',
    sorter: (a, b) => a.calls - b.calls,
    render: (v: number | string) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(); },
  },
  {
    title: 'Mean (s)',
    dataIndex: 'mean_ms',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.mean_ms - b.mean_ms,
    render: (v: number | string) => { const n = Number(v) / 1000; return <Text type={n > 1 ? 'danger' : n > 0.2 ? 'warning' : undefined}>{isNaN(n) ? v : n.toFixed(3)}</Text>; },
  },
  {
    title: 'Max (s)',
    dataIndex: 'max_ms',
    sorter: (a, b) => a.max_ms - b.max_ms,
    render: (v: number | string) => { const n = Number(v) / 1000; return isNaN(n) ? v : n.toFixed(3); },
  },
  {
    title: 'Total (s)',
    dataIndex: 'total_ms',
    sorter: (a, b) => a.total_ms - b.total_ms,
    render: (v: number | string) => { const n = Number(v) / 1000; return isNaN(n) ? v : n.toFixed(1); },
  },
  {
    title: 'Rows',
    dataIndex: 'rows',
    sorter: (a, b) => a.rows - b.rows,
    render: (v: number | string) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(); },
  },
  {
    title: 'Cache Hit %',
    dataIndex: 'hit_percent',
    sorter: (a, b) => a.hit_percent - b.hit_percent,
    render: (v: number | string) => { const n = Number(v); return (
      <Tag color={n >= 99 ? 'green' : n >= 90 ? 'orange' : 'red'}>{isNaN(n) ? v : n.toFixed(1)}%</Tag>
    ); },
  },
]

const missingIndexColumns: ColumnsType<MissingIndex> = [
  { title: 'Table', dataIndex: 'table_name' },
  {
    title: 'Seq Scans',
    dataIndex: 'seq_scan',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.seq_scan - b.seq_scan,
    render: (v: number | string) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(); },
  },
  {
    title: 'Index Scans',
    dataIndex: 'idx_scan',
    sorter: (a, b) => a.idx_scan - b.idx_scan,
    render: (v: number | string) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(); },
  },
  {
    title: 'Difference',
    dataIndex: 'diff',
    sorter: (a, b) => a.diff - b.diff,
    render: (v: number | string) => { const n = Number(v); return <Text type="danger">{isNaN(n) ? v : n.toLocaleString()}</Text>; },
  },
  {
    title: 'Rows',
    dataIndex: 'row_count',
    render: (v: number | string) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(); },
  },
  { title: 'Size', dataIndex: 'table_size' },
]

const connectionColumns: ColumnsType<ActiveConnection> = [
  { title: 'PID', dataIndex: 'pid', width: 80 },
  { title: 'User', dataIndex: 'username' },
  { title: 'App', dataIndex: 'application_name' },
  {
    title: 'State',
    dataIndex: 'state',
    render: (state: string) => {
      const color = state === 'active' ? 'green' : state === 'idle' ? 'default' : 'orange'
      return <Tag color={color}>{state}</Tag>
    },
  },
  {
    title: 'Duration (s)',
    dataIndex: 'query_duration_sec',
    sorter: (a, b) => (a.query_duration_sec ?? 0) - (b.query_duration_sec ?? 0),
    render: (v: number | null) => (v !== null && v > 5 ? <Text type="danger">{v}</Text> : v),
  },
  {
    title: 'Query',
    dataIndex: 'query',
    width: '35%',
    render: (text: string) => (
      <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>
        {text?.length > 150 ? text.slice(0, 150) + '...' : text}
      </Text>
    ),
  },
]

const bloatColumns: ColumnsType<TableBloat> = [
  { title: 'Table', dataIndex: 'table_name' },
  {
    title: 'Live Tuples',
    dataIndex: 'n_live_tup',
    render: (v: number | string) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(); },
  },
  {
    title: 'Dead Tuples',
    dataIndex: 'n_dead_tup',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.n_dead_tup - b.n_dead_tup,
    render: (v: number | string) => { const n = Number(v); return <Text type="warning">{isNaN(n) ? v : n.toLocaleString()}</Text>; },
  },
  {
    title: 'Dead %',
    dataIndex: 'dead_percent',
    sorter: (a, b) => a.dead_percent - b.dead_percent,
    render: (v: number | string) => { const n = Number(v); return (
      <Tag color={n > 20 ? 'red' : n > 10 ? 'orange' : 'green'}>{isNaN(n) ? v : n.toFixed(1)}%</Tag>
    ); },
  },
  { title: 'Size', dataIndex: 'table_size' },
  { title: 'Last Vacuum', dataIndex: 'last_autovacuum', render: (v: string | null) => v ?? '—' },
]

const unusedIndexColumns: ColumnsType<UnusedIndex> = [
  { title: 'Index', dataIndex: 'index_name' },
  { title: 'Table', dataIndex: 'table_name' },
  { title: 'Scans', dataIndex: 'scans' },
  { title: 'Size', dataIndex: 'index_size' },
]

const lockColumns: ColumnsType<WaitingLock> = [
  { title: 'Blocked PID', dataIndex: 'blocked_pid' },
  { title: 'Blocked User', dataIndex: 'blocked_user' },
  {
    title: 'Blocked Query',
    dataIndex: 'blocked_query',
    render: (text: string) => <Text code style={{ fontSize: 11 }}>{text}</Text>,
  },
  { title: 'Blocking PID', dataIndex: 'blocking_pid' },
  { title: 'Blocking User', dataIndex: 'blocking_user' },
  {
    title: 'Blocking Query',
    dataIndex: 'blocking_query',
    render: (text: string) => <Text code style={{ fontSize: 11 }}>{text}</Text>,
  },
]

const formatAge = (v: string | null): React.ReactNode => {
  if (!v) return '—'
  const hours = (Date.now() - new Date(v).getTime()) / 3600000
  if (hours > 168) return <Text type="danger">{v}</Text>
  if (hours > 24) return <Text type="warning">{v}</Text>
  return v
}

const vacuumColumns: ColumnsType<VacuumStatusRow> = [
  { title: 'Table', dataIndex: 'table_name' },
  {
    title: 'Live Tuples',
    dataIndex: 'n_live_tup',
    render: (v: number | string) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString(); },
  },
  {
    title: 'Dead Tuples',
    dataIndex: 'n_dead_tup',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.n_dead_tup - b.n_dead_tup,
    render: (v: number | string) => { const n = Number(v); return <Text type={n > 10000 ? 'danger' : n > 1000 ? 'warning' : undefined}>{isNaN(n) ? v : n.toLocaleString()}</Text>; },
  },
  {
    title: 'Dead %',
    dataIndex: 'dead_percent',
    sorter: (a, b) => a.dead_percent - b.dead_percent,
    render: (v: number | string) => { const n = Number(v); return <Tag color={n > 20 ? 'red' : n > 5 ? 'orange' : 'green'}>{isNaN(n) ? v : n.toFixed(1)}%</Tag>; },
  },
  { title: 'Last Vacuum', dataIndex: 'last_vacuum', render: formatAge },
  { title: 'Last Autovacuum', dataIndex: 'last_autovacuum', render: formatAge },
  { title: 'Last Analyze', dataIndex: 'last_autoanalyze', render: formatAge },
  {
    title: 'Vacuum Count',
    dataIndex: 'vacuum_count',
    render: (v: number, r: VacuumStatusRow) => `${v} / ${r.autovacuum_count}`,
  },
  {
    title: 'Reloptions',
    dataIndex: 'reloptions',
    render: (v: string | null) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : '—',
  },
]

const workerProcessColumns: ColumnsType<WorkerProcess> = [
  { title: 'Backend Type', dataIndex: 'backend_type' },
  {
    title: 'Count',
    dataIndex: 'count',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.count - b.count,
  },
]

const pgSettingColumns: ColumnsType<PgSetting> = [
  { title: 'Setting', dataIndex: 'name' },
  {
    title: 'Value',
    dataIndex: 'setting',
    render: (v: string, r: PgSetting) => r.unit ? `${v} ${r.unit}` : v,
  },
  { title: 'Category', dataIndex: 'category' },
  { title: 'Description', dataIndex: 'short_desc' },
]

export const Diagnostics: React.FC = () => {
  const data = useStore($diagnostics)
  const loading = useStore($diagnosticsLoading)

  useEffect(() => {
    diagnosticsMounted()
  }, [])

  const handleRefresh = () => {
    getDiagnostics({ url: '/api/v1/diagnostics' })
  }

  return (
    <Spin spinning={loading}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Typography.Title level={5} style={{ margin: 0 }}>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            Database Diagnostics
          </Typography.Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </Col>
      </Row>

      {data?.database_size && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic title="Database Size" value={data.database_size.size} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Connections"
                value={data.database_size.active_connections}
                suffix={`/ ${data.database_size.max_connections}`}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Waiting Locks"
                value={data.waiting_locks?.length ?? 0}
                valueStyle={data.waiting_locks?.length > 0 ? { color: '#cf1322' } : undefined}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Tables Missing Indexes"
                value={data.missing_indexes?.length ?? 0}
                valueStyle={data.missing_indexes?.length > 5 ? { color: '#faad14' } : undefined}
              />
            </Card>
          </Col>
        </Row>
      )}

      {data?.waiting_locks && data.waiting_locks.length > 0 && (
        <Alert
          message="Active Lock Contention"
          description={`${data.waiting_locks.length} queries are waiting for locks`}
          type="error"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Collapse
        defaultActiveKey={['slow_queries', 'missing_indexes']}
        items={[
          {
            key: 'slow_queries',
            label: `Slow Queries (${data?.slow_queries?.length ?? 0})`,
            children: (
              <Table<SlowQuery>
                columns={slowQueryColumns}
                dataSource={data?.slow_queries ?? []}
                rowKey={(r) => r.query}
                size="small"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 900 }}
              />
            ),
          },
          {
            key: 'missing_indexes',
            label: `Missing Indexes (${data?.missing_indexes?.length ?? 0})`,
            children: (
              <Table<MissingIndex>
                columns={missingIndexColumns}
                dataSource={data?.missing_indexes ?? []}
                rowKey={(r) => r.table_name}
                size="small"
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'active_connections',
            label: `Active Connections (${data?.active_connections?.length ?? 0})`,
            children: (
              <Table<ActiveConnection>
                columns={connectionColumns}
                dataSource={data?.active_connections ?? []}
                rowKey={(r) => String(r.pid)}
                size="small"
                pagination={false}
                scroll={{ x: 900 }}
              />
            ),
          },
          {
            key: 'table_bloat',
            label: `Table Bloat (${data?.table_bloat?.length ?? 0})`,
            children: (
              <Table<TableBloat>
                columns={bloatColumns}
                dataSource={data?.table_bloat ?? []}
                rowKey={(r) => r.table_name}
                size="small"
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'index_usage',
            label: `Unused Indexes (${data?.index_usage?.length ?? 0})`,
            children: (
              <Table<UnusedIndex>
                columns={unusedIndexColumns}
                dataSource={data?.index_usage ?? []}
                rowKey={(r) => r.index_name}
                size="small"
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'waiting_locks',
            label: `Waiting Locks (${data?.waiting_locks?.length ?? 0})`,
            children: (
              <Table<WaitingLock>
                columns={lockColumns}
                dataSource={data?.waiting_locks ?? []}
                rowKey={(r) => `${r.blocked_pid}-${r.blocking_pid}`}
                size="small"
                pagination={false}
                scroll={{ x: 800 }}
              />
            ),
          },
          {
            key: 'vacuum_status',
            label: `Vacuum Status (${data?.vacuum_status?.length ?? 0})`,
            children: (
              <Table<VacuumStatusRow>
                columns={vacuumColumns}
                dataSource={data?.vacuum_status ?? []}
                rowKey={(r) => `${r.schemaname}.${r.table_name}`}
                size="small"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1100 }}
              />
            ),
          },
          {
            key: 'parallel_workers',
            label: 'Parallel Workers',
            children: (
              <>
                {data?.parallel_workers && (
                  <Row gutter={16} style={{ marginBottom: 12 }}>
                    {data.parallel_workers.settings.map((s) => (
                      <Col key={s.name} span={6}>
                        <Card size="small">
                          <Statistic title={s.name} value={s.setting} />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
                <Table<WorkerProcess>
                  columns={workerProcessColumns}
                  dataSource={data?.parallel_workers?.processes ?? []}
                  rowKey={(r) => r.backend_type}
                  size="small"
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: 'pg_configuration',
            label: `PG Configuration (${data?.pg_configuration?.length ?? 0})`,
            children: (
              <Table<PgSetting>
                columns={pgSettingColumns}
                dataSource={data?.pg_configuration ?? []}
                rowKey={(r) => r.name}
                size="small"
                pagination={false}
              />
            ),
          },
        ]}
      />
    </Spin>
  )
}
